import { useCallback, useEffect, useRef, useState } from "react";
import type { HoverPanelAnchor } from "../components/HoverPanel/HoverPanel";

/** Space a panel needs above its trigger before it opens below it instead. */
const SPACE_ABOVE_PX = 260;

/** Anchor used until the trigger has been laid out (jsdom, first paint). */
export const UNMEASURED_ANCHOR: HoverPanelAnchor = {
  x: 0,
  y: 0,
  placement: "top",
};

/**
 * Anchor centred on a trigger's box, opening below it when it sits too close to the top.
 * `xPercent` slides the anchored point along the box: the score rail points at a score on
 * its track rather than at the middle of it. `yAlign` picks the vertical edge — heatmap
 * column headers pin to `"top"` so the panel sits above the flag, not mid-header.
 */
export function anchorForRect(
  rect: DOMRect | undefined,
  xPercent = 50,
  yAlign: "center" | "top" | "bottom" = "center",
): HoverPanelAnchor {
  if (!rect || rect.height === 0) return UNMEASURED_ANCHOR;
  const y =
    yAlign === "top"
      ? rect.top
      : yAlign === "bottom"
        ? rect.bottom
        : rect.top + rect.height / 2;
  return {
    x: rect.left + (rect.width * xPercent) / 100,
    y,
    placement: rect.top >= SPACE_ABOVE_PX ? "top" : "bottom",
  };
}

export interface HoverPanelAnchoring {
  anchor: HoverPanelAnchor;
  /**
   * Point the panel at a trigger, remembering how to find it again. Call this as the panel
   * opens — `measure` runs immediately and on every scroll or resize while it stays open.
   */
  pinTo: (measure: () => HoverPanelAnchor) => void;
}

/**
 * Keeps a viewport-anchored hover panel pinned to its trigger. Panels are fixed so they
 * escape the scroll containers and cards that would clip them, which means nothing moves
 * them for us: the anchor is re-measured whenever the page shifts underneath.
 */
export function useHoverPanelAnchor(isOpen: boolean): HoverPanelAnchoring {
  const [anchor, setAnchor] = useState<HoverPanelAnchor>(UNMEASURED_ANCHOR);
  const measureRef = useRef<(() => HoverPanelAnchor) | null>(null);

  const pinTo = useCallback((measure: () => HoverPanelAnchor) => {
    measureRef.current = measure;
    setAnchor(measure());
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const reanchor = () => {
      const measure = measureRef.current;
      if (measure) setAnchor(measure());
    };
    // Capture phase: the trigger may sit inside a scrolling container, not just the window.
    window.addEventListener("scroll", reanchor, true);
    window.addEventListener("resize", reanchor);
    return () => {
      window.removeEventListener("scroll", reanchor, true);
      window.removeEventListener("resize", reanchor);
    };
  }, [isOpen]);

  return { anchor, pinTo };
}
